import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Anchor,
  CaretDown,
  CaretRight,
  CheckCircle,
  Clock,
  GearSix,
  Info,
} from "@phosphor-icons/react";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useLocale, useTranslations } from "next-intl";
import {
  formatMgLabel,
  formatSgLabel,
  type CatalogGroupsLocale,
} from "@navi-soft/catalog-groups";
import { Button } from "@navi-soft/ui/button";
import {
  ECatalog,
  ECatalogSchematicPlaceholder,
  type ECatalogColumn,
  type ECatalogNavGroup,
} from "@navi-soft/ui/e-catalog";
import { TextField } from "@navi-soft/ui/text-field";
import {
  ForumPageChrome,
  ForumPageShell,
} from "@/components/forum-page-chrome";
import {
  addCartLine,
  fetchCatalog,
  fetchCatalogPlatforms,
  fetchCatalogYards,
  fetchMe,
  fetchPlatformCatalog,
  fetchPlatformSchematic,
  findHulls,
  type CatalogItem,
  type CatalogPlatform,
  type CatalogSchematicHotspot,
  type CatalogYard,
} from "@/lib/api";
import { CatalogSchematicOverlay } from "@/components/catalog-schematic-overlay";
import { cn } from "@/lib/cn";
import { humanizeHint } from "@/lib/humanize-hint";

type CatalogSearch = {
  yard?: string;
  platform?: string;
  win?: string;
};

function optionCodesLabel(codes: string[] | undefined): string {
  const list = (codes ?? []).map((c) => c.trim()).filter(Boolean);
  return list.length > 0 ? list.join(" · ") : "—";
}

function hullOptionBlocked(item: CatalogItem): boolean {
  return Boolean(item.option_codes?.some((c) => c.trim())) && !item.in_as_built;
}

export const Route = createFileRoute("/catalog")({
  validateSearch: (search: Record<string, unknown>): CatalogSearch => ({
    yard: typeof search.yard === "string" ? search.yard : undefined,
    platform: typeof search.platform === "string" ? search.platform : undefined,
    win: typeof search.win === "string" ? search.win : undefined,
  }),
  component: CatalogPage,
});

function CatalogPage() {
  const t = useTranslations("app");
  const { yard, platform, win } = Route.useSearch();

  const meQuery = useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const res = await fetchMe();
      if (!res.ok) throw new Error(res.error);
      return res.data;
    },
  });
  const authenticated = Boolean(meQuery.data?.authenticated);

  const mode: "yards" | "etka" | "hull" =
    yard && platform ? "etka" : win ? "hull" : "yards";

  if (mode === "etka" && yard && platform) {
    return (
      <ForumPageShell flush>
        {!authenticated ? (
          <p className="p-5 text-ui-dense text-[var(--brand-text-muted)]">
            {t("homeNeedLogin")}
          </p>
        ) : (
          <EtkaStep yard={yard} platform={platform} win={win} />
        )}
      </ForumPageShell>
    );
  }

  const title =
    mode === "yards" ? t("catalogTitle") : t("catalogHullTitle");

  const intro =
    mode === "yards" ? t("catalogIntroYards") : t("catalogIntroHull");

  return (
    <ForumPageShell>
      <ForumPageChrome
        eyebrow={t("catalogEyebrow")}
        title={title}
        intro={intro}
        trailing={
          mode === "hull" ? (
            <CatalogBreadcrumb yard={yard} platform={platform} win={win} />
          ) : null
        }
      />

      {!authenticated ? (
        <p className="text-ui-dense text-[var(--brand-text-muted)]">
          {t("homeNeedLogin")}
        </p>
      ) : mode === "yards" ? (
        <YardsStep initialExpanded={yard} />
      ) : win ? (
        <HullWinRedirect win={win} />
      ) : null}
    </ForumPageShell>
  );
}

function CatalogBreadcrumb({
  yard,
  platform,
  win,
}: {
  yard?: string;
  platform?: string;
  win?: string;
}) {
  const t = useTranslations("app");
  return (
    <nav
      aria-label={t("catalogBreadcrumbAria")}
      className="mt-1 flex flex-wrap items-center justify-end gap-1 text-ui-micro text-[var(--brand-text-muted)]"
    >
      <Link
        to="/catalog"
        className="text-[var(--brand-navy)] underline-offset-2 hover:underline"
      >
        {t("catalogCrumbYards")}
      </Link>
      {yard ? (
        <>
          <CaretRight className="size-3" />
          <Link
            to="/catalog"
            search={{ yard }}
            className="text-[var(--brand-navy)] underline-offset-2 hover:underline"
          >
            {yard}
          </Link>
        </>
      ) : null}
      {platform ? (
        <>
          <CaretRight className="size-3" />
          <span className="text-[var(--brand-navy)]">{platform}</span>
        </>
      ) : null}
      {win ? (
        <>
          <CaretRight className="size-3" />
          <span className="font-mono text-[var(--brand-navy)]">{win}</span>
        </>
      ) : null}
    </nav>
  );
}

function micFromYard(systemName: string): string {
  const cleaned = systemName
    .replace(/^(yard-|platform-)/i, "")
    .replace(/[^a-zA-Z0-9]/g, "");
  return (cleaned.slice(0, 3) || "YRD").toUpperCase();
}

function YardsStep({ initialExpanded }: { initialExpanded?: string }) {
  const t = useTranslations("app");
  const [expanded, setExpanded] = useState<string | null>(
    initialExpanded ?? null,
  );

  const query = useQuery({
    queryKey: ["catalog", "yards"],
    queryFn: async () => {
      const res = await fetchCatalogYards();
      if (!res.ok) throw new Error(res.error);
      return res.data.items;
    },
  });

  const yards = query.data ?? [];
  const platformQueries = useQueries({
    queries: yards.map((yard) => ({
      queryKey: ["catalog", "platforms", yard.system_name],
      queryFn: async () => {
        const res = await fetchCatalogPlatforms(yard.system_name);
        if (!res.ok) throw new Error(res.error);
        return res.data.items;
      },
      enabled: yards.length > 0,
    })),
  });

  if (query.isLoading) {
    return <p className="text-ui-dense text-[var(--brand-text-muted)]">{t("loading")}</p>;
  }
  if (query.isError) {
    return (
      <p className="text-ui-dense text-[var(--brand-text-muted)]">{t("catalogError")}</p>
    );
  }
  if (!yards.length) {
    return (
      <p className="text-ui-dense text-[var(--brand-text-muted)]">
        {t("catalogYardsEmpty")}
      </p>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-card)] shadow-subtle">
      <div className="flex items-center gap-3 border-b border-[var(--brand-border)] px-4 py-3 sm:px-6">
        <Anchor
          className="size-4 shrink-0 text-[var(--brand-navy)]"
          weight="bold"
        />
        <span className="text-ui-micro font-semibold text-[var(--brand-navy)]">
          {t("catalogMembers")}
        </span>
        <span className="ml-auto text-ui-micro text-[var(--brand-text-muted)]">
          {t("catalogYardsUnit", { count: yards.length })}
        </span>
      </div>

      <ul className="divide-y divide-[var(--brand-border)]">
        {yards.map((yard, i) => {
          const open = expanded === yard.system_name;
          const platforms = platformQueries[i]?.data ?? [];
          const platformsLoading = platformQueries[i]?.isLoading;
          return (
            <li key={yard.system_name}>
              <button
                type="button"
                aria-expanded={open}
                onClick={() =>
                  setExpanded(open ? null : yard.system_name)
                }
                className="flex w-full flex-col gap-3 px-4 py-4 text-left outline-none transition-colors hover:bg-[color-mix(in_srgb,var(--brand-bg-section)_50%,transparent)] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[rgb(var(--brand-navy-rgb)/0.3)] sm:flex-row sm:items-center sm:px-6"
              >
                <div className="flex min-w-0 flex-1 items-start gap-2">
                  <span className="mt-0.5 shrink-0 text-[var(--brand-text-muted)]">
                    {open ? (
                      <CaretDown className="size-3.5" weight="bold" />
                    ) : (
                      <CaretRight className="size-3.5" weight="bold" />
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <span className="font-heading text-ui-dense font-semibold text-[var(--brand-navy)]">
                        {yard.display_name}
                      </span>
                      <span className="font-mono text-ui-micro text-[var(--brand-text-muted)]">
                        {t("catalogMic", { code: micFromYard(yard.system_name) })}
                      </span>
                      <YardStatusBadge
                        status={
                          yard.platform_count > 0 ? "active" : "onboarding"
                        }
                      />
                    </div>
                    <p className="text-ui-micro text-[var(--brand-text-muted)]">
                      {t("catalogYardSite", {
                        yard: humanizeHint(yard.system_name),
                      })}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1.5 pl-5 sm:pl-0">
                  {platformsLoading ? (
                    <span className="text-ui-micro text-[var(--brand-text-muted)]">
                      …
                    </span>
                  ) : platforms.length > 0 ? (
                    platforms.slice(0, 4).map((p) => (
                      <span
                        key={p.platform_id}
                        className="rounded bg-[var(--brand-bg-section)] px-2 py-0.5 text-ui-micro font-medium text-[var(--brand-navy)]"
                      >
                        {p.display_name}
                      </span>
                    ))
                  ) : (
                    <span className="text-ui-micro text-[var(--brand-text-muted)]">
                      {t("catalogPlatformsEmpty")}
                    </span>
                  )}
                  {platforms.length > 4 ? (
                    <span className="rounded bg-[var(--brand-bg-section)] px-2 py-0.5 text-ui-micro text-[var(--brand-text-muted)]">
                      +{platforms.length - 4}
                    </span>
                  ) : null}
                </div>

                <span className="whitespace-nowrap pl-5 text-ui-micro text-[var(--brand-text-muted)] sm:pl-0 sm:text-right">
                  {t("catalogYardNote", {
                    platforms: yard.platform_count,
                    hulls: yard.hull_count,
                  })}
                </span>
              </button>

              {open ? (
                <YardAccordionBody
                  yard={yard}
                  platforms={platforms}
                  loading={Boolean(platformsLoading)}
                />
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function YardStatusBadge({ status }: { status: "active" | "onboarding" }) {
  const t = useTranslations("app");
  if (status === "onboarding") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-ui-micro text-amber-700">
        <Clock className="size-2.5" weight="bold" />
        {t("catalogStatusOnboarding")}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-ui-micro text-emerald-700">
      <CheckCircle className="size-2.5" weight="fill" />
      {t("catalogStatusActive")}
    </span>
  );
}

function YardAccordionBody({
  yard,
  platforms,
  loading,
}: {
  yard: CatalogYard;
  platforms: CatalogPlatform[];
  loading: boolean;
}) {
  const t = useTranslations("app");
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="border-t border-[var(--brand-border)] bg-[var(--brand-bg-section)] px-4 py-3 sm:px-6">
        <p className="text-ui-dense text-[var(--brand-text-muted)]">{t("loading")}</p>
      </div>
    );
  }
  if (!platforms.length) {
    return (
      <div className="border-t border-[var(--brand-border)] bg-[var(--brand-bg-section)] px-4 py-3 sm:px-6">
        <p className="text-ui-dense text-[var(--brand-text-muted)]">
          {t("catalogPlatformsEmpty")}
        </p>
      </div>
    );
  }

  return (
    <div className="border-t border-[var(--brand-border)] bg-[var(--brand-bg-section)] px-4 py-3 sm:px-6">
      <p className="mb-2 text-ui-micro font-semibold uppercase tracking-[0.1em] text-[var(--brand-text-muted)]">
        {t("catalogPlatformsTitle")}
      </p>
      <ul className="flex flex-col gap-2">
        {platforms.map((p) => (
          <li
            key={p.platform_id}
            className="flex flex-col gap-2 rounded-lg border border-[var(--brand-border)] bg-[var(--brand-surface-card)] px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="min-w-0">
              <p className="font-heading text-ui-dense font-semibold text-[var(--brand-navy)]">
                {p.display_name}
                {p.platform_version ? (
                  <span className="ml-1.5 font-mono text-ui-micro font-normal text-[var(--brand-text-muted)]">
                    v{p.platform_version}
                  </span>
                ) : null}
              </p>
              <p className="font-mono text-ui-micro text-[var(--brand-text-muted)]">
                {p.platform_id}
                {" · "}
                {t("catalogHullsCount", { count: p.hull_count })}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => {
                  void navigate({
                    to: "/catalog",
                    search: {
                      yard: yard.system_name,
                      platform: p.platform_id,
                    },
                  });
                }}
              >
                {t("catalogOpenEtka")}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  void navigate({
                    to: "/configure",
                    search: {
                      yard: yard.system_name,
                      platform: p.platform_id,
                    },
                  });
                }}
              >
                {t("catalogOpenConfigure")}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  void navigate({ to: "/find" });
                }}
              >
                {t("catalogActionFind")}
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function catalogLocale(raw: string): CatalogGroupsLocale {
  return raw === "en" ? "en" : "ru";
}

function etkaMgLabel(
  t: ReturnType<typeof useTranslations<"app">>,
  locale: CatalogGroupsLocale,
  mg: number,
): string {
  return formatMgLabel(mg, locale) ?? t("catalogMgLabel", { n: mg });
}

function etkaSgLabel(
  t: ReturnType<typeof useTranslations<"app">>,
  locale: CatalogGroupsLocale,
  mg: number,
  sg: number,
): string {
  return formatSgLabel(mg, sg, locale) ?? t("catalogSgLabel", { n: sg });
}

type PlateRow = CatalogItem & { plate_mark: string };

function emptyPlateItem(
  partNumber: string,
  mg: number | null,
  sg: number | null,
): CatalogItem {
  return {
    part_number: partNumber,
    name: null,
    mg,
    sg,
    in_as_built: false,
    qty: null,
    offer: null,
    catalog_role: "platform",
    superseded: false,
    successor_part_number: null,
    option_codes: [],
  };
}

function plateRowsFromHotspots(
  hotspots: CatalogSchematicHotspot[],
  catalogItems: CatalogItem[],
  mg: number | null,
  sg: number | null,
): PlateRow[] {
  const byPn = new Map<string, CatalogItem>();
  for (const item of catalogItems) {
    const pn = item.part_number.trim();
    if (pn && !byPn.has(pn)) byPn.set(pn, item);
  }
  return hotspots.map((hot) => {
    const pn = hot.part_number?.trim() ?? "";
    const found = pn ? byPn.get(pn) : undefined;
    return {
      ...(found ?? emptyPlateItem(pn, mg, sg)),
      plate_mark: hot.mark,
    };
  });
}

function schematicHotspots(
  raw: CatalogSchematicHotspot[] | undefined,
): CatalogSchematicHotspot[] {
  return Array.isArray(raw) ? raw : [];
}

function EtkaStep({
  yard,
  platform,
  win,
}: {
  yard: string;
  platform: string;
  win?: string;
}) {
  const t = useTranslations("app");
  const locale = catalogLocale(useLocale());
  const [mg, setMg] = useState<number | null>(null);
  const [sg, setSg] = useState<number | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
  const queryClient = useQueryClient();

  const catalogQuery = useQuery({
    queryKey: ["catalog", "platform", yard, platform, win ?? ""],
    queryFn: async () => {
      const res = await fetchPlatformCatalog(yard, platform, "", win ?? "");
      if (!res.ok) throw new Error(res.error);
      return res.data;
    },
  });

  const items = catalogQuery.data?.items ?? [];
  const platformItems = items.filter((i) => i.catalog_role !== "accessory");
  const accessoryItems = items.filter((i) => i.catalog_role === "accessory");

  const groups = useMemo(() => {
    const map = new Map<number, Map<number, CatalogItem[]>>();
    for (const item of platformItems) {
      const g = item.mg ?? 0;
      const s = item.sg ?? 0;
      if (!map.has(g)) map.set(g, new Map());
      const sgMap = map.get(g)!;
      if (!sgMap.has(s)) sgMap.set(s, []);
      sgMap.get(s)!.push(item);
    }
    const listed = [...map.entries()]
      .sort(([a], [b]) => a - b)
      .map(([mgKey, sgMap]) => ({
        mg: mgKey,
        accessories: false,
        subgroups: [...sgMap.entries()]
          .sort(([a], [b]) => a - b)
          .map(([sgKey, parts]) => ({ sg: sgKey, parts })),
      }));
    if (accessoryItems.length > 0) {
      listed.push({
        mg: -1,
        accessories: true,
        subgroups: [{ sg: 0, parts: accessoryItems }],
      });
    }
    return listed;
  }, [platformItems, accessoryItems]);

  const activeMg = mg ?? groups[0]?.mg ?? null;
  const activeGroup = groups.find((g) => g.mg === activeMg) ?? null;
  const activeSg = sg ?? activeGroup?.subgroups[0]?.sg ?? null;
  const mgSgParts =
    activeSg != null
      ? (activeGroup?.subgroups.find((s) => s.sg === activeSg)?.parts ?? [])
      : (activeGroup?.subgroups.flatMap((s) => s.parts) ?? items);

  const schematicQuery = useQuery({
    queryKey: ["catalog", "schematic", yard, platform, activeMg, activeSg],
    enabled:
      activeMg != null &&
      activeSg != null &&
      activeMg >= 0 &&
      !activeGroup?.accessories,
    queryFn: async () => {
      const res = await fetchPlatformSchematic(
        yard,
        platform,
        activeMg as number,
        activeSg as number,
      );
      if (res.ok) return res.data;
      return null;
    },
  });

  const schematicSvgUrl = useMemo(() => {
    const svg = schematicQuery.data?.svg?.trim();
    if (!svg) return null;
    return URL.createObjectURL(new Blob([svg], { type: "image/svg+xml" }));
  }, [schematicQuery.data?.svg]);

  useEffect(() => {
    return () => {
      if (schematicSvgUrl) URL.revokeObjectURL(schematicSvgUrl);
    };
  }, [schematicSvgUrl]);

  const plateHotspots = schematicHotspots(schematicQuery.data?.hotspots);
  const tableParts: PlateRow[] = useMemo(() => {
    if (
      plateHotspots.length > 0 &&
      activeMg != null &&
      activeMg >= 0 &&
      !activeGroup?.accessories
    ) {
      return plateRowsFromHotspots(
        plateHotspots,
        items,
        activeMg,
        activeSg,
      );
    }
    return mgSgParts.map((item) => ({
      ...item,
      plate_mark: "",
    }));
  }, [
    activeGroup?.accessories,
    activeMg,
    activeSg,
    items,
    mgSgParts,
    plateHotspots,
  ]);

  const overlayCallouts = useMemo(
    () =>
      plateHotspots.map((hot) => ({
        id: hot.mark,
        mark: hot.mark,
        partNumber: hot.part_number?.trim() || null,
        anchorX: hot.anchor_x,
        anchorY: hot.anchor_y,
        labelX: hot.label_x,
        labelY: hot.label_y,
      })),
    [plateHotspots],
  );

  const dimmedCalloutIds = useMemo(() => {
    if (!win) return [];
    return tableParts
      .filter((row) => hullOptionBlocked(row) && row.plate_mark)
      .map((row) => row.plate_mark);
  }, [tableParts, win]);

  const hoveredCalloutId = useMemo(() => {
    if (hoveredRowIndex == null) return null;
    const row = tableParts[hoveredRowIndex];
    if (!row) return null;
    if (row.plate_mark) return row.plate_mark;
    const pn = row.part_number.trim();
    if (!pn) return null;
    return overlayCallouts.find((c) => c.partNumber === pn)?.id ?? null;
  }, [hoveredRowIndex, overlayCallouts, tableParts]);

  const addMutation = useMutation({
    mutationFn: async (item: CatalogItem) => {
      const offer = item.offer;
      if (!offer) throw new Error("no_offer");
      const res = await addCartLine({
        part_number: item.part_number,
        system_name: offer.system_name || yard,
        qty: 1,
      });
      if (!res.ok) throw new Error(res.error);
      return res.data;
    },
    onSuccess: async () => {
      setHint(t("catalogAdded"));
      await queryClient.invalidateQueries({ queryKey: ["cart"] });
    },
    onError: (err: Error) => {
      setHint(
        err.message === "trade_required"
          ? t("catalogTradeRequired")
          : t("catalogAddFailed"),
      );
    },
  });

  function stockLabel(band: string | null | undefined): string {
    if (!band) return "—";
    if (band === "in_stock") return t("stockIn");
    if (band === "low") return t("stockLow");
    if (band === "out") return t("stockOut");
    return band;
  }

  function stockTone(band: string | null | undefined): string {
    if (band === "in_stock") return "bg-emerald-50 text-emerald-700";
    if (band === "low" || band === "on_order") return "bg-amber-50 text-amber-700";
    if (band === "out") return "bg-slate-100 text-slate-600";
    return "bg-[var(--brand-bg-section)] text-[var(--brand-text-muted)]";
  }

  if (catalogQuery.isLoading) {
    return (
      <p className="flex min-h-0 flex-1 items-center justify-center text-ui-dense text-[var(--brand-text-muted)]">
        {t("loading")}
      </p>
    );
  }
  if (catalogQuery.isError) {
    return (
      <p className="flex min-h-0 flex-1 items-center justify-center p-5 text-ui-dense text-[var(--brand-text-muted)]">
        {catalogQuery.error.message === "platform_not_found"
          ? t("catalogPlatformNotFound")
          : catalogQuery.error.message === "hull_not_found"
            ? t("catalogHullNotFound")
            : t("catalogError")}
      </p>
    );
  }

  const navGroups: ECatalogNavGroup[] = groups.map((g) => ({
    id: String(g.mg),
    label: g.accessories ? t("catalogAccessories") : etkaMgLabel(t, locale, g.mg),
    leaf: g.accessories,
    subgroups: g.subgroups.map((s) => ({
      id: String(s.sg),
      label: etkaSgLabel(t, locale, g.mg, s.sg),
      count:
        !g.accessories &&
        g.mg === activeMg &&
        s.sg === activeSg &&
        plateHotspots.length > 0
          ? plateHotspots.length
          : s.parts.length,
    })),
  }));

  const columns: ECatalogColumn<PlateRow>[] = [
    {
      id: "mark",
      header: "#",
      cell: (item) => (
        <span className="font-mono text-[var(--brand-text-muted)]">
          {item.plate_mark || "·"}
        </span>
      ),
    },
    {
      id: "pn",
      header: t("catalogColPn"),
      cell: (item) => (
        <span className="whitespace-nowrap font-mono font-medium text-[var(--brand-navy)]">
          {item.part_number || "—"}
        </span>
      ),
    },
    {
      id: "name",
      header: t("catalogColName"),
      cell: (item) => (
        <span className="text-[var(--brand-navy)]">
          {item.name || "—"}
          {item.superseded && item.successor_part_number ? (
            <span className="mt-0.5 block text-ui-nano font-normal text-[var(--brand-text-muted)]">
              {t("catalogSuperseded", { pn: item.successor_part_number })}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      id: "option",
      header: t("catalogColOption"),
      cell: (item) => (
        <span className="font-mono text-[var(--brand-text-muted)]">
          {optionCodesLabel(item.option_codes)}
        </span>
      ),
    },
    {
      id: "stock",
      header: t("catalogColStock"),
      cell: (item) => (
        <span
          className={cn(
            "rounded px-2 py-0.5 text-ui-micro font-medium",
            stockTone(item.offer?.stock_band),
          )}
        >
          {stockLabel(item.offer?.stock_band)}
        </span>
      ),
    },
    {
      id: "price",
      header: t("catalogColPrice"),
      align: "right",
      cell: (item) => (
        <span className="whitespace-nowrap font-medium text-[var(--brand-navy)]">
          {item.offer?.unit_price
            ? `${item.offer.unit_price} ${item.offer.currency || ""}`.trim()
            : "—"}
          {item.offer?.eta_hint ? (
            <span className="mt-0.5 block text-ui-nano font-normal text-[var(--brand-text-muted)]">
              {item.offer.eta_hint}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      id: "cart",
      header: "",
      cell: (item) =>
        item.offer && item.part_number ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            title={
              win && hullOptionBlocked(item)
                ? t("catalogNotOnThisHull", {
                    codes: optionCodesLabel(item.option_codes),
                  })
                : item.offer.can_add_to_cart
                  ? undefined
                  : t("catalogTradeRequired")
            }
            disabled={
              (Boolean(win) && hullOptionBlocked(item)) ||
              !item.offer.can_add_to_cart ||
              addMutation.isPending
            }
            onClick={() => {
              setHint(null);
              addMutation.mutate(item);
            }}
          >
            {t("catalogAddCart")}
          </Button>
        ) : (
          "—"
        ),
    },
  ];

  return (
    <ECatalog
      breadcrumb={
        <>
          <Link
            to="/catalog"
            className="font-medium text-[var(--brand-navy)] hover:underline"
          >
            {t("catalogCrumbYards")}
          </Link>
          <CaretRight className="size-3 shrink-0" />
          <Link
            to="/catalog"
            search={{ yard }}
            className="font-medium text-[var(--brand-navy)] hover:underline"
          >
            {yard}
          </Link>
          <CaretRight className="size-3 shrink-0" />
          <span className="font-medium text-[var(--brand-navy)]">{platform}</span>
          {win ? (
            <>
              <CaretRight className="size-3 shrink-0" />
              <span className="font-mono text-[var(--brand-navy)]">{win}</span>
            </>
          ) : null}
          {activeMg != null ? (
            <>
              <CaretRight className="size-3 shrink-0" />
              <span>{etkaMgLabel(t, locale, activeMg)}</span>
            </>
          ) : null}
        </>
      }
      breadcrumbTrailing={
        win ? (
          <Link
            to="/catalog"
            search={{ yard, platform }}
            className="font-medium text-[var(--brand-navy)] hover:underline"
          >
            {t("catalogOpenFullPlatform")}
          </Link>
        ) : undefined
      }
      hint={hint ?? (win ? t("catalogIntroWinEtka") : undefined)}
      isEmpty={items.length === 0}
      empty={t("catalogEmpty")}
      groups={navGroups}
      activeGroupId={activeMg != null ? String(activeMg) : null}
      activeSubgroupId={activeSg != null ? String(activeSg) : null}
      onSelectGroup={(id) => {
        setMg(Number(id));
        setSg(null);
      }}
      onSelectSubgroup={(_groupId, subgroupId) => setSg(Number(subgroupId))}
      columns={columns}
      rows={tableParts}
      rowKey={(item, index) =>
        item.plate_mark
          ? `mark:${item.plate_mark}`
          : item.part_number || `row:${index}`
      }
      rowDomId={(item, index) =>
        item.plate_mark
          ? `etka-row-${item.plate_mark}`
          : item.part_number
            ? `etka-row-${item.part_number}`
            : `etka-row-${index}`
      }
      hoveredRowIndex={hoveredRowIndex}
      onHoveredRowIndexChange={setHoveredRowIndex}
      rowClassName={(item) =>
        win && hullOptionBlocked(item) ? "opacity-45" : undefined
      }
      schematic={
        schematicSvgUrl ? (
          <div className="relative h-full w-full bg-white">
            <img
              src={schematicSvgUrl}
              alt={t("catalogSchematicTitle")}
              className="h-full w-full object-contain"
              draggable={false}
            />
            <CatalogSchematicOverlay
              callouts={overlayCallouts}
              hoveredId={hoveredCalloutId}
              dimmedIds={dimmedCalloutIds}
              onHoverId={(id) => {
                if (!id) {
                  setHoveredRowIndex(null);
                  return;
                }
                const index = tableParts.findIndex(
                  (row) => row.plate_mark === id,
                );
                setHoveredRowIndex(index >= 0 ? index : null);
              }}
              onSelectId={(id) => {
                document
                  .getElementById(`etka-row-${id}`)
                  ?.scrollIntoView({ block: "nearest", behavior: "smooth" });
              }}
            />
          </div>
        ) : undefined
      }
      schematicEmpty={
        <ECatalogSchematicPlaceholder
          icon={
            <GearSix
              className="mx-auto mb-2 size-10 text-[var(--brand-border)]"
              weight="light"
            />
          }
          title={t("catalogSchematicTitle")}
          caption={t("catalogSchematicPlaceholder")}
        />
      }
      footnote={
        <>
          <Info className="size-3" />
          {win ? t("catalogFootnoteWin") : t("catalogFootnote")}
        </>
      }
    />
  );
}

function HullWinRedirect({ win }: { win: string }) {
  const t = useTranslations("app");
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ["hulls", "find", win],
    queryFn: async () => {
      const res = await findHulls(win, 1);
      if (!res.ok) throw new Error(res.error);
      return res.data.items[0] ?? null;
    },
  });

  useEffect(() => {
    const hull = query.data;
    if (!hull?.system_name?.trim() || !hull.platform_id?.trim()) return;
    void navigate({
      to: "/catalog",
      search: {
        yard: hull.system_name,
        platform: hull.platform_id,
        win: hull.identifier_value,
      },
      replace: true,
    });
  }, [navigate, query.data]);

  if (query.isLoading) {
    return (
      <p className="text-ui-dense text-[var(--brand-text-muted)]">{t("loading")}</p>
    );
  }
  if (query.isError) {
    return (
      <p className="text-ui-dense text-[var(--brand-text-muted)]">
        {t("catalogError")}
      </p>
    );
  }
  if (!query.data) {
    return (
      <p className="text-ui-dense text-[var(--brand-text-muted)]">
        {t("catalogHullNotFound")}
      </p>
    );
  }
  if (!query.data.system_name?.trim() || !query.data.platform_id?.trim()) {
    return <HullCatalogStep win={win} />;
  }
  return (
    <p className="text-ui-dense text-[var(--brand-text-muted)]">{t("loading")}</p>
  );
}

function HullCatalogStep({ win }: { win: string }) {
  const t = useTranslations("app");
  const [q, setQ] = useState("");
  const [submittedQ, setSubmittedQ] = useState("");
  const [hint, setHint] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const catalogQuery = useQuery({
    queryKey: ["catalog", "hull", win, submittedQ],
    queryFn: async () => {
      const res = await fetchCatalog(win, submittedQ);
      if (!res.ok) throw new Error(res.error);
      return res.data;
    },
  });

  const addMutation = useMutation({
    mutationFn: async (item: CatalogItem) => {
      const offer = item.offer;
      if (!offer) throw new Error("no_offer");
      const res = await addCartLine({
        part_number: item.part_number,
        system_name: offer.system_name,
        qty: 1,
      });
      if (!res.ok) throw new Error(res.error);
      return res.data;
    },
    onSuccess: async () => {
      setHint(t("catalogAdded"));
      await queryClient.invalidateQueries({ queryKey: ["cart"] });
    },
    onError: (err: Error) => {
      setHint(
        err.message === "trade_required"
          ? t("catalogTradeRequired")
          : t("catalogAddFailed"),
      );
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmittedQ(q.trim());
  }

  function stockLabel(band: string | null | undefined): string {
    if (!band) return "—";
    if (band === "in_stock") return t("stockIn");
    if (band === "low") return t("stockLow");
    if (band === "out") return t("stockOut");
    return band;
  }

  const hull = catalogQuery.data?.hull;

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-3">
      {hull ? (
        <div className="flex flex-wrap items-end justify-between gap-3 rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-card)] px-4 py-3 shadow-subtle">
          <div>
            <p className="text-ui-nano font-semibold uppercase tracking-[0.12em] text-[var(--brand-text-muted)]">
              {t("catalogHullBadge")}
            </p>
            <p className="font-mono text-[17px] font-semibold text-[var(--brand-navy)]">
              {hull.identifier_value}
            </p>
            <p className="mt-1 text-ui-micro text-[var(--brand-text-muted)]">
              {humanizeHint(hull.system_name)}
              {hull.platform_id ? ` · ${hull.platform_id}` : ""}
              {hull.hull_number ? ` · ${hull.hull_number}` : ""}
            </p>
          </div>
          {hull.system_name && hull.platform_id ? (
            <Link
              to="/catalog"
              search={{ yard: hull.system_name, platform: hull.platform_id }}
              className="text-ui-dense font-medium text-[var(--brand-navy)] underline-offset-2 hover:underline"
            >
              {t("catalogOpenFullPlatform")}
            </Link>
          ) : null}
        </div>
      ) : null}

      <form
        onSubmit={onSubmit}
        className="flex flex-wrap items-end gap-2 rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-card)] px-3 py-2 shadow-subtle"
      >
        <TextField
          label={t("catalogSearchLabel")}
          size="md"
          className="min-w-52 flex-1"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("catalogSearchPlaceholder")}
        />
        <Button type="submit" variant="secondary" size="md">
          {t("catalogSearchSubmit")}
        </Button>
      </form>

      {hint ? (
        <p className="text-ui-dense text-[var(--brand-text-muted)]">{hint}</p>
      ) : null}

      {catalogQuery.isLoading ? (
        <p className="text-ui-dense text-[var(--brand-text-muted)]">{t("loading")}</p>
      ) : null}
      {catalogQuery.isError ? (
        <p className="text-ui-dense text-[var(--brand-text-muted)]">
          {catalogQuery.error.message === "hull_not_found"
            ? t("catalogHullNotFound")
            : t("catalogError")}
        </p>
      ) : null}
      {catalogQuery.isSuccess && catalogQuery.data.items.length === 0 ? (
        <p className="text-ui-dense text-[var(--brand-text-muted)]">
          {t("catalogEmpty")}
        </p>
      ) : null}

      {catalogQuery.isSuccess && catalogQuery.data.items.length > 0 ? (
        <div className="overflow-x-auto rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-card)] shadow-subtle">
          <table className="w-full min-w-[40rem] border-collapse text-left text-ui-dense">
            <thead className="bg-[var(--brand-navy)] text-ui-micro text-[var(--brand-bg-page)]">
              <tr>
                <th className="px-3 py-2 font-medium">{t("catalogColPn")}</th>
                <th className="px-3 py-2 font-medium">{t("catalogColName")}</th>
                <th className="px-3 py-2 font-medium">{t("catalogColOption")}</th>
                <th className="px-3 py-2 font-medium">{t("catalogColPrice")}</th>
                <th className="px-3 py-2 font-medium">{t("catalogColStock")}</th>
                <th className="px-3 py-2 font-medium">{t("catalogColEta")}</th>
                <th className="px-3 py-2 font-medium">{t("catalogColAsBuilt")}</th>
                <th className="px-3 py-2 font-medium" />
              </tr>
            </thead>
            <tbody>
              {catalogQuery.data.items.map((item) => {
                const offer = item.offer;
                const blocked = hullOptionBlocked(item);
                const blockTitle = blocked
                  ? t("catalogNotOnThisHull", {
                      codes: optionCodesLabel(item.option_codes),
                    })
                  : undefined;
                return (
                  <tr
                    key={item.part_number}
                    className={cn(
                      "border-t border-[var(--brand-border)]",
                      blocked && "opacity-45",
                    )}
                    title={blockTitle}
                  >
                    <td className="px-3 py-2 font-mono text-[var(--brand-navy)]">
                      {item.part_number}
                    </td>
                    <td className="px-3 py-2 text-[var(--brand-text-muted)]">
                      {item.name || "—"}
                    </td>
                    <td className="px-3 py-2 font-mono text-[var(--brand-text-muted)]">
                      {optionCodesLabel(item.option_codes)}
                    </td>
                    <td className="px-3 py-2 font-mono text-[var(--brand-text-muted)]">
                      {offer?.unit_price
                        ? `${offer.unit_price} ${offer.currency || ""}`.trim()
                        : "—"}
                    </td>
                    <td className="px-3 py-2 text-[var(--brand-text-muted)]">
                      {stockLabel(offer?.stock_band)}
                    </td>
                    <td className="px-3 py-2 text-[var(--brand-text-muted)]">
                      {offer?.eta_hint || "—"}
                    </td>
                    <td className="px-3 py-2 text-[var(--brand-text-muted)]">
                      {item.in_as_built
                        ? item.qty != null
                          ? String(item.qty)
                          : t("catalogYes")
                        : "—"}
                    </td>
                    <td className="px-3 py-2">
                      {offer ? (
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={
                            blocked ||
                            !offer.can_add_to_cart ||
                            addMutation.isPending
                          }
                          title={
                            blocked
                              ? blockTitle
                              : offer.can_add_to_cart
                                ? undefined
                                : t("catalogTradeRequired")
                          }
                          onClick={() => {
                            setHint(null);
                            addMutation.mutate(item);
                          }}
                        >
                          {t("catalogAddCart")}
                        </Button>
                      ) : (
                        <span className="text-ui-micro text-[var(--brand-text-muted)]">
                          —
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
